import React, { useState } from "react";
import { BRAND } from "../../utils/tourUtils";

// Localized strings
const STRINGS = {
  en: {
    subtitle: "Cofresí's Treasure Hunt",
    locations: "Locations",
    hours: "Hours",
    miles: "Miles",
    howItWorks: "How It Works",
    startingPoint: "Starting Point",
    beginAdventure: "Begin Adventure",
    howItWorksTitle: "How It Works",
    step1: "Follow the directions to each location",
    step2: "At each stop, solve Cofresí's riddle",
    step3: "Use the Answer Tracker to follow your progress, uncover a final clue, and win a prize!",
    gotIt: "Got It",
    startingPointTitle: "Starting Point",
    startingPointName: "Old San Juan Cruise Port",
    startingPointNear: "Near Hotel Rumbao",
    startingPointDesc: "Begin your adventure at the Old San Juan Cruise Port. Look for the blue building marked \"Hotel Rumbao\" to get started.",
    mapCaption: "Starting Point: Old San Juan Cruise Port (Near Hotel Rumbao)",
  },
  es: {
    subtitle: "La Búsqueda del Tesoro de Cofresí",
    locations: "Ubicaciones",
    hours: "Horas",
    miles: "Millas",
    howItWorks: "Cómo Funciona",
    startingPoint: "Punto de Partida",
    beginAdventure: "Comenzar Aventura",
    howItWorksTitle: "Cómo Funciona",
    step1: "Sigue las direcciones a cada ubicación",
    step2: "En cada parada, resuelve el acertijo de Cofresí",
    step3: "Usa la navegación para seguir tu progreso, descubre la pista final y ¡gana un premio!",
    gotIt: "Entendido",
    startingPointTitle: "Punto de Partida",
    startingPointName: "Puerto de Cruceros del Viejo San Juan",
    startingPointNear: "Cerca del Hotel Rumbao",
    startingPointDesc: "Comienza tu aventura en el Puerto de Cruceros del Viejo San Juan. Busca el edificio azul marcado \"Hotel Rumbao\" para comenzar.",
    mapCaption: "Punto de Partida: Puerto de Cruceros del Viejo San Juan (Cerca del Hotel Rumbao)",
  }
};

export function WelcomeSplash({ onStart, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const [showMapModal, setShowMapModal] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showStartingPoint, setShowStartingPoint] = useState(false);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: '#000000' }}>
      <style>{`
        .ios-system-font {
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif;
        }
        .frosted-glass {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .frosted-glass-dark {
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
        }
      `}</style>

      {/* Hero Image */}
      <div className="absolute inset-0">
        <img
          src="/images/sanjuan.webp"
          alt="San Juan, Puerto Rico"
          className="w-full h-full object-cover object-top"
        />
        {/* Stronger gradient overlay for better readability */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.2) 20%, rgba(242,242,247,0.85) 45%, rgba(242,242,247,0.98) 65%, rgb(242,242,247) 80%)'
        }}></div>
      </div>

      {/* Floating Top Button */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-end p-4 safe-area-inset-top">
        <button
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: 'none',
            cursor: 'pointer'
          }}
          onClick={() => window.history.back()}
          aria-label="Exit tour"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <line x1="2" y1="2" x2="14" y2="14" />
            <line x1="14" y1="2" x2="2" y2="14" />
          </svg>
        </button>
      </div>

      {/* Center Content - positioned lower for better readability on solid background */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 text-center" style={{ marginTop: '40px' }}>
        {/* Badge and Flag Row */}
        <div className="flex items-center mb-6">
          {/* Puerto Rico Flag Circle */}
          <div
            className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              border: '2px solid #000000',
              zIndex: 1
            }}
          >
            <img
              src="/images/puerto-rico-flag.png"
              alt="Puerto Rico flag"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Badge Image */}
          {BRAND?.logoUrl && (
            <img
              src={BRAND.logoUrl}
              alt="Prize badge"
              className="h-20 w-auto object-contain"
              style={{
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                marginLeft: '-12px',
                zIndex: 2,
                position: 'relative'
              }}
            />
          )}
        </div>

        <h1 className="ios-system-font text-4xl sm:text-5xl font-bold mb-2 whitespace-nowrap" style={{
          color: '#000000',
          letterSpacing: '-0.5px'
        }}>
          San Juan, Puerto Rico
        </h1>
        <p className="ios-system-font text-lg mb-6" style={{
          color: '#000000'
        }}>
          {t.subtitle}
        </p>
        <div className="inline-flex items-center gap-4 px-6 py-3 rounded-full" style={{
          background: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <div className="flex flex-col items-center" style={{ width: '70px' }}>
            <span className="ios-system-font text-lg font-bold" style={{ color: '#000000' }}>12</span>
            <span className="ios-system-font text-xs" style={{ color: '#6b7280' }}>{t.locations}</span>
          </div>
          <div style={{ width: '1px', height: '32px', background: 'rgba(0, 0, 0, 0.1)' }}></div>
          <div className="flex flex-col items-center" style={{ width: '70px' }}>
            <span className="ios-system-font text-lg font-bold" style={{ color: '#000000' }}>2-3</span>
            <span className="ios-system-font text-xs" style={{ color: '#6b7280' }}>{t.hours}</span>
          </div>
          <div style={{ width: '1px', height: '32px', background: 'rgba(0, 0, 0, 0.1)' }}></div>
          <div className="flex flex-col items-center" style={{ width: '70px' }}>
            <span className="ios-system-font text-lg font-bold" style={{ color: '#000000' }}>1.5</span>
            <span className="ios-system-font text-xs" style={{ color: '#6b7280' }}>{t.miles}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons Row - positioned on solid background */}
      <div className="absolute z-10 left-0 right-0 px-6" style={{ bottom: '140px' }}>
        <div className="flex items-center justify-center gap-8">
          <button
            onClick={() => setShowHowItWorks(true)}
            className="flex flex-col items-center gap-2"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
              background: '#ffffff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            </div>
            <span className="ios-system-font text-xs font-medium" style={{ color: '#000000' }}>
              {t.howItWorks}
            </span>
          </button>

          <button
            onClick={() => setShowStartingPoint(true)}
            className="flex flex-col items-center gap-2"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
              background: '#ffffff',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span className="ios-system-font text-xs font-medium" style={{ color: '#000000' }}>
              {t.startingPoint}
            </span>
          </button>
        </div>
      </div>

      {/* Bottom Button */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 pb-8">
        <div className="mx-auto max-w-lg">
          <button
            onClick={onStart}
            className="w-full ios-system-font font-semibold text-base"
            style={{
              background: '#000000',
              color: '#ffffff',
              borderRadius: '13px',
              padding: '18px',
              border: 'none',
              cursor: 'pointer',
              minHeight: '56px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            }}
          >
            {t.beginAdventure}
          </button>
        </div>
      </div>

      {/* How It Works Modal - iOS Sheet */}
      {showHowItWorks && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
          }}
          onClick={() => setShowHowItWorks(false)}
        >
          <div
            className="w-full max-w-lg mx-auto rounded-t-3xl overflow-hidden"
            style={{
              background: '#ffffff',
              maxHeight: '80vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div style={{
                width: '36px',
                height: '5px',
                borderRadius: '10px',
                background: '#c7c7cc'
              }}></div>
            </div>

            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 50px)' }}>
              <h2 className="ios-system-font text-2xl font-bold mb-4" style={{ color: '#000000' }}>
                {t.howItWorksTitle}
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex gap-3">
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ios-system-font"
                    style={{
                      background: '#000000',
                      color: '#ffffff',
                    }}
                  >
                    1
                  </span>
                  <div>
                    <p className="ios-system-font text-base" style={{ color: '#000000' }}>
                      {t.step1}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ios-system-font"
                    style={{
                      background: '#000000',
                      color: '#ffffff',
                    }}
                  >
                    2
                  </span>
                  <div>
                    <p className="ios-system-font text-base" style={{ color: '#000000' }}>
                      {t.step2}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ios-system-font"
                    style={{
                      background: '#000000',
                      color: '#ffffff',
                    }}
                  >
                    3
                  </span>
                  <div>
                    <p className="ios-system-font text-base" style={{ color: '#000000' }}>
                      {t.step3}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowHowItWorks(false)}
                className="w-full ios-system-font font-semibold text-base mb-4"
                style={{
                  background: '#007AFF',
                  color: '#ffffff',
                  borderRadius: '13px',
                  padding: '16px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t.gotIt}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Starting Point Modal - iOS Sheet */}
      {showStartingPoint && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
          }}
          onClick={() => setShowStartingPoint(false)}
        >
          <div
            className="w-full max-w-lg mx-auto rounded-t-3xl overflow-hidden"
            style={{
              background: '#ffffff',
              maxHeight: '80vh',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div style={{
                width: '36px',
                height: '5px',
                borderRadius: '10px',
                background: '#c7c7cc'
              }}></div>
            </div>

            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 50px)' }}>
              <h2 className="ios-system-font text-2xl font-bold mb-4" style={{ color: '#000000' }}>
                {t.startingPointTitle}
              </h2>

              <div className="mb-6">
                <img
                  src="/images/starting-point-sanjuan.webp"
                  alt="Map of starting point"
                  className="w-full rounded-xl mb-4"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                />

                <div className="ios-system-font text-lg font-semibold mb-2" style={{ color: '#000000' }}>
                  {t.startingPointName}
                </div>
                <div className="ios-system-font text-base mb-4" style={{ color: '#8e8e93' }}>
                  {t.startingPointNear}
                </div>

                <p className="ios-system-font text-sm" style={{ color: '#8e8e93' }}>
                  {t.startingPointDesc}
                </p>
              </div>

              <button
                onClick={() => setShowStartingPoint(false)}
                className="w-full ios-system-font font-semibold text-base mb-4"
                style={{
                  background: '#007AFF',
                  color: '#ffffff',
                  borderRadius: '13px',
                  padding: '16px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t.gotIt}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMapModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
          onClick={() => setShowMapModal(false)}
        >
          <div
            className="relative max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowMapModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
              aria-label="Close map"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Map image */}
            <img
              src="/images/starting-point-sanjuan.webp"
              alt="Map of starting point - Old San Juan Cruise Port"
              className="w-full h-auto rounded-xl"
              style={{
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
              }}
            />

            {/* Caption */}
            <div
              className="mt-4 text-center p-3 rounded-lg"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              <p className="text-white font-medium text-sm ios-system-font">
                {t.mapCaption}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
